import { Crud } from "@/api";
import { UploadsApi } from "@/api/uploads.api";
import { UserApi } from "@/api/user.api";
import { createLineItemJson } from "@/stores/actions/createLineItem";
import { withMultiWindowBorder } from "@/stores/multiWindow";
import {
  $activeAdminCartId,
  clearActiveAdminCartId,
  setActiveAdminCartId,
} from "@/stores/adminWorkspace";
import {
  $sharedCartSession,
  clearSharedCartSession,
} from "@/stores/sharedCart";
import { changeDims } from "@/stores/actions/toggleShowDims";
import { updateRenderView } from "@/stores/actions/updateRenderView";
import { $selectedImage } from "@/stores/selectedImage";
import {
  $visualizer,
  waitForCropUpload,
} from "@/stores/visualizer";
import { Cart, LineItem } from "@/utils/types/types";
import html2canvas from "html2canvas-pro";
import Cookies from "js-cookie";
import { map } from "nanostores";
import hash from "object-hash";

// A 404/403 from a managed-cart endpoint means the saved workspace or shared
// cart no longer exists server-side (deleted, converted to an order, expired
// share link, or created against a different environment). getCart() already
// recovers from this; the line-item methods below use this helper to do the
// same instead of surfacing an unhandled AxiosError (Sentry TPW-ASTRO-WEB-UI-1K).
function isCartGoneError(error: unknown) {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === 404 || status === 403;
}

export const $cart = map({
  id: "",
  lineItems: [] as LineItem[],
  createdAt: undefined as unknown as Date,
  discount: null as number | null,
  name: null as string | null,
  message: null as string | null,
  isShareable: null as boolean | null,
  initialLoad: true,
  isOpen: false,
});

export const CartService = {
  isUsingAdminWorkspace() {
    return Boolean($activeAdminCartId.get());
  },
  async createAdminWorkspaceCart(name?: string) {
    const cart = await Crud.carts.create({
      isShareable: true,
      name: name?.trim() || "",
    } as any);
    setActiveAdminCartId(cart.id);
    await CartService.getCart();
    return cart.id;
  },
  async switchAdminWorkspaceCart(cartId: string) {
    setActiveAdminCartId(cartId);
    await CartService.getCart();
  },
  async clearAdminWorkspace() {
    clearActiveAdminCartId();
    await CartService.getCart();
  },
  async getCart() {
    const data = $cart.get();
    const activeAdminCartId = $activeAdminCartId.get();

    if (activeAdminCartId) {
      try {
        const adminCart = await Crud.carts.get(activeAdminCartId);
        $cart.set({ ...data, ...adminCart });
        return;
      } catch (error) {
        // If workspace cart is inaccessible/missing, fall back to user cart.
        clearActiveAdminCartId();
      }
    }

    const { cartId: sharedCartId, secret: sharedSecret } =
      $sharedCartSession.get();
    if (sharedCartId && sharedSecret) {
      try {
        const sharedCart = await Crud.carts.get(sharedCartId, {
          shareSecret: sharedSecret,
        } as any);
        $cart.set({ ...data, ...sharedCart });
        return;
      } catch (error) {
        // If shared cart is inaccessible/expired, clear the session and fall back.
        clearSharedCartSession();
      }
    }

    const cart = await UserApi.getCart();
    $cart.set({ ...data, ...cart.data.userCart });
  },
  async update(payload: Partial<Cart>) {
    const data = $cart.get();
    // Phase 2 (2026-08-16): the backend carts gate now requires owner /
    // admin / shareSecret. In a shared-cart session we are neither owner
    // nor admin, so present the secret with the patch. (Writing the same
    // shareSecret value back is a harmless no-op.)
    const { cartId: sharedCartId, secret: sharedSecret } =
      $sharedCartSession.get();
    const authedPayload =
      sharedCartId && sharedSecret && data.id === sharedCartId
        ? { ...payload, shareSecret: sharedSecret }
        : payload;
    const cart = await Crud.carts.update(data.id, authedPayload);
    $cart.set({ ...data, ...cart });
  },
  // ── Blank-capture guard (2026-08-12 PM) ────────────────────────────────────
  // A cart preview came back as a pure-white JPEG (Muyassar K., cart
  // 6a7c94421b1e…): the html2canvas screenshot fired while the canvas had
  // nothing useful on it — most likely the straight-on view-reset racing a
  // texture reload on a slow device/connection (the 500+200ms settle is a
  // guess, not a guarantee). Detect a featureless capture and retry a few
  // times before accepting it; report to Sentry so frequency is visible.
  // Works on the LIVE WebGL canvas too (preserveDrawingBuffer is on), which
  // is what waitForCanvasReady below relies on.
  captureIsBlank(captured: HTMLCanvasElement): boolean {
    try {
      const w = 50;
      const h = 50;
      const probe = document.createElement("canvas");
      probe.width = w;
      probe.height = h;
      const ctx = probe.getContext("2d");
      if (!ctx) return false;
      ctx.drawImage(captured, 0, 0, w, h);
      const d = ctx.getImageData(0, 0, w, h).data;
      const r0 = d[0];
      const g0 = d[1];
      const b0 = d[2];
      let uniform = 0;
      for (let i = 0; i < d.length; i += 4) {
        if (
          Math.abs(d[i] - r0) < 6 &&
          Math.abs(d[i + 1] - g0) < 6 &&
          Math.abs(d[i + 2] - b0) < 6
        ) {
          uniform++;
        }
      }
      // >99.5% of pixels within a hair of one flat color = featureless frame.
      return uniform / (w * h) > 0.995;
    } catch {
      // Probing must never break add-to-cart — treat unprobeable as fine.
      return false;
    }
  },
  // ── Render-ready wait (2026-08-13) ─────────────────────────────────────────
  // Successor to guessed settle times: before screenshotting, actively wait
  // until the WebGL canvas provably has content. The render loop paints on
  // every animation frame, so awaiting two rAFs forces at least one fresh
  // renderer.render() into the preserved drawing buffer between probes; the
  // probe then reads real pixels off the live canvas. Also waits out the
  // renderer's opacity fade-in (initThree starts the canvas at opacity 0 and
  // gsap-fades it in on LoadingManager.onLoad — html2canvas honors opacity,
  // so capturing mid-fade washes the preview toward blank white even when
  // the buffer itself has content). Times out rather than blocking
  // add-to-cart; the retry+Sentry backstop below still runs after.
  async waitForCanvasReady(canvasEl: any, timeoutMs = 5000): Promise<boolean> {
    if (!canvasEl) return false;
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise<void>(resolve => {
        // Double-rAF = at least one full render pass between probes. The
        // setTimeout race keeps this from hanging add-to-cart if rAF ever
        // stalls (hidden/backgrounded tab).
        const timer = setTimeout(resolve, 300);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            clearTimeout(timer);
            resolve();
          })
        );
      });
      let faded = false;
      try {
        faded =
          parseFloat(window.getComputedStyle(canvasEl).opacity || "1") < 0.98;
      } catch {
        faded = false; // probing must never block add-to-cart
      }
      if (!faded && !CartService.captureIsBlank(canvasEl)) return true;
    }
    (window as any).Sentry?.captureMessage(
      "cart preview canvas not render-ready after 5s wait (capturing anyway)",
      { level: "warning" }
    );
    return false;
  },
  // ── Direct buffer copy (2026-08-12 evening) ────────────────────────────────
  // Sentry TPW-ASTRO-WEB-UI-3W proved the remaining blank-capture mechanism:
  // waitForCanvasReady PASSED (live WebGL canvas provably had pixels; no
  // companion "not render-ready" event) yet html2canvas-pro returned a blank
  // screenshot 3x for a real customer (Chrome/Mac, Spectrum NYC). So the
  // screenshot LIBRARY is the failure point, not timing. The capture target
  // is a single <canvas> with preserveDrawingBuffer on — the same drawImage
  // the blank-probe already performs successfully — so copy the buffer
  // directly and skip html2canvas entirely. html2canvas remains only as a
  // fallback if the direct copy probes blank/fails.
  captureCanvasDirect(canvasEl: any): HTMLCanvasElement | null {
    try {
      if (!canvasEl || typeof canvasEl.getContext !== "function") return null;
      // Draw at the element's CSS size (matches what html2canvas produced);
      // the source buffer may be devicePixelRatio-scaled and larger.
      const w = canvasEl.clientWidth || canvasEl.width;
      const h = canvasEl.clientHeight || canvasEl.height;
      if (!w || !h) return null;
      const copy = document.createElement("canvas");
      copy.width = w;
      copy.height = h;
      const ctx = copy.getContext("2d");
      if (!ctx) return null;
      // JPEG has no alpha — flatten onto white (scene background is white).
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(canvasEl, 0, 0, w, h);
      return copy;
    } catch {
      // Any failure just means we fall back to the html2canvas path.
      return null;
    }
  },
  async captureCanvasWithRetry(canvasEl: any): Promise<any> {
    await CartService.waitForCanvasReady(canvasEl);
    const direct = CartService.captureCanvasDirect(canvasEl);
    if (direct && !CartService.captureIsBlank(direct)) return direct;
    if (direct) {
      // Buffer copy worked mechanically but probed blank — unexpected given
      // the ready-wait; surface it and let html2canvas have a try.
      (window as any).Sentry?.captureMessage(
        "cart preview direct canvas copy probed blank — falling back to html2canvas",
        { level: "warning" }
      );
    }
    let captured = await html2canvas(canvasEl);
    for (let attempt = 1; attempt <= 3; attempt++) {
      if (!CartService.captureIsBlank(captured)) return captured;
      console.warn(`[cart] blank preview capture — retry ${attempt}/3`);
      await new Promise(resolve => setTimeout(resolve, 700));
      captured = await html2canvas(canvasEl);
    }
    if (CartService.captureIsBlank(captured)) {
      (window as any).Sentry?.captureMessage(
        "cart preview captured blank after 3 retries (customer will see a blank thumbnail)",
        { level: "warning" }
      );
    }
    return captured;
  },
  async addLineItem() {
    Cookies.remove("showDimensions");
    const visualizer = $visualizer.get();

    const canvasEl: any = document.getElementById("canvas");
    const noteEl: any = document.getElementById("input-note");
    const selectedImage = $selectedImage.get();
    // The order preview is a live screenshot of the 3D canvas, so whatever
    // render view was active at ADD TO CART became the order image — customers
    // adding from Room View got the room mockup on their packing slip
    // (2026-08-12). Force the straight-on product view for the capture, then
    // restore the customer's view.
    const prevRenderView = visualizer.selectedRenderView;
    const needsViewReset = !prevRenderView?.startsWith("Straight");
    if (needsViewReset) {
      updateRenderView("Straight");
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    changeDims(false);
    await new Promise(resolve => setTimeout(resolve, 200));
    await CartService.captureCanvasWithRetry(canvasEl).then(async (c: any) => {
      changeDims(true);
      if (needsViewReset) updateRenderView(prevRenderView);
      const base64Image = c.toDataURL("image/jpeg");

      // Extract the content type and base64 data from the string
      const parts = base64Image.split(";base64,");
      const contentType = parts[0].split(":")[1];
      const rawBase64 = parts[1];

      // Decode the base64 data into binary data
      const decodedData = atob(rawBase64);

      // Create a Uint8Array from the binary data
      const uint8Array = new Uint8Array(decodedData.length);
      for (let i = 0; i < decodedData.length; ++i) {
        uint8Array[i] = decodedData.charCodeAt(i);
      }

      // Create a Blob object from the Uint8Array and content type
      const blob = new Blob([uint8Array], { type: contentType });

      // Create a File object from the Blob
      const itemJson = createLineItemJson({ strict: true });
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "For debugging purposes not sending line item to server in dev mode."
        );
      }
      const imageHash = `${hash(itemJson)}.jpg`;

      const previewImageFile = new File([blob], imageHash, {
        type: contentType,
      });
      const previewImageUploadResponse = await UploadsApi.uploadFile({
        file: previewImageFile,
        type: "cart",
      });
      const previewImageUrl = previewImageUploadResponse.data.fileUrl;
      // The crop upload may still be in flight (collapse-from-Sizes → ADD TO
      // CART is seconds apart). Wait for it, then read the LIVE store — the
      // `visualizer` snapshot above predates the upload (staging 26790).
      await waitForCropUpload();
      const imageCrop = $visualizer.get().imageCrop;
      const croppedImageUrl = imageCrop.url || "";
      // Full-res crop rectangle (fractions of the preview) — the order build
      // crops the RAW with it. Only meaningful with a cropped URL. 2026-09-09.
      const cropRect =
        croppedImageUrl && imageCrop.rect ? imageCrop.rect : null;

      const payload = {
        adminCustom: false,
        adminEmail: "",
        isCropped: Boolean(croppedImageUrl),
        itemJson: {
          ...itemJson,
          imageUrl: previewImageUrl,
          rawImageUrl: selectedImage?.rawUrl,
          compressedImageUrl: selectedImage?.url,
          croppedImageUrl: croppedImageUrl,
          cropRect,
          previewImageUrl,
          imageHash,
        },
        note: noteEl?.value || "",
      };
      const activeAdminCartId = $activeAdminCartId.get();
      const { cartId: sharedCartId, secret: sharedSecret } =
        $sharedCartSession.get();
      if (activeAdminCartId) {
        try {
          await Crud.carts.addLineItem(activeAdminCartId, payload as any);
        } catch (error) {
          if (!isCartGoneError(error)) throw error;
          // The saved workspace cart no longer exists. Clear it and tell the
          // admin, rather than silently adding to their own master cart
          // (items should never be added to the default admin cart).
          clearActiveAdminCartId();
          await CartService.getCart();
          throw new Error(
            "That workspace cart no longer exists, so it was cleared. Please create or select a cart and try again."
          );
        }
      } else if (sharedCartId) {
        try {
          await Crud.carts.addLineItem(sharedCartId, {
            ...(payload as any),
            shareSecret: sharedSecret,
          });
        } catch (error) {
          if (!isCartGoneError(error)) throw error;
          // The shared cart is gone or the link expired. Fall back to the
          // user's own cart, mirroring getCart()'s recovery.
          clearSharedCartSession();
          await UserApi.addLineItem(payload);
        }
      } else {
        await UserApi.addLineItem(payload);
      }
      await CartService.getCart();
    });
  },
  async updateLineItem(
    itemId: string,
    originalQuantity: number,
    originalNote: string
  ) {
    Cookies.remove("showDimensions");
    const visualizer = $visualizer.get();

    const canvasEl: any = document.getElementById("canvas");
    const selectedImage = $selectedImage.get();
    // Same straight-on capture rule as addLineItem (2026-08-12).
    const prevRenderView = visualizer.selectedRenderView;
    const needsViewReset = !prevRenderView?.startsWith("Straight");
    if (needsViewReset) {
      updateRenderView("Straight");
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    changeDims(false);
    await new Promise(resolve => setTimeout(resolve, 200));
    await CartService.captureCanvasWithRetry(canvasEl).then(async (c: any) => {
      changeDims(true);
      if (needsViewReset) updateRenderView(prevRenderView);
      const base64Image = c.toDataURL("image/jpeg");

      // Extract the content type and base64 data from the string
      const parts = base64Image.split(";base64,");
      const contentType = parts[0].split(":")[1];
      const rawBase64 = parts[1];

      // Decode the base64 data into binary data
      const decodedData = atob(rawBase64);

      // Create a Uint8Array from the binary data
      const uint8Array = new Uint8Array(decodedData.length);
      for (let i = 0; i < decodedData.length; ++i) {
        uint8Array[i] = decodedData.charCodeAt(i);
      }

      // Create a Blob object from the Uint8Array and content type
      const blob = new Blob([uint8Array], { type: contentType });

      // Create a File object from the Blob
      const itemJson = createLineItemJson({ strict: true });
      if (process.env.NODE_ENV === "development") {
        console.warn("For debugging purposes updating line item in dev mode.");
      }
      const imageHash = `${hash(itemJson)}.jpg`;

      const previewImageFile = new File([blob], imageHash, {
        type: contentType,
      });
      const previewImageUploadResponse = await UploadsApi.uploadFile({
        file: previewImageFile,
        type: "cart",
      });
      const previewImageUrl = previewImageUploadResponse.data.fileUrl;
      await waitForCropUpload();
      const imageCrop = $visualizer.get().imageCrop;
      const croppedImageUrl = imageCrop.url || "";
      const cropRect =
        croppedImageUrl && imageCrop.rect ? imageCrop.rect : null;

      const payload = {
        id: itemId,
        itemJson: {
          ...itemJson,
          previewImageUrl,
          imageUrl: previewImageUrl,
          compressedImageUrl: selectedImage?.url,
          rawImageUrl: selectedImage?.rawUrl,
          croppedImageUrl,
          cropRect,
          imageHash,
        },
        quantity: originalQuantity,
        note: originalNote,
      };
      const activeAdminCartId = $activeAdminCartId.get();
      const { cartId: sharedCartId, secret: sharedSecret } =
        $sharedCartSession.get();
      try {
        if (activeAdminCartId) {
          await Crud.carts.updateLineItem(activeAdminCartId, itemId, payload);
        } else if (sharedCartId) {
          await Crud.carts.updateLineItem(sharedCartId, itemId, {
            ...payload,
            shareSecret: sharedSecret,
          } as any);
        } else {
          await UserApi.updateLineItem(payload);
        }
      } catch (error) {
        if (!isCartGoneError(error) || (!activeAdminCartId && !sharedCartId)) {
          throw error;
        }
        // The managed cart backing this item is gone — clear the stale
        // session and refresh so the UI falls back to the user's own cart.
        if (activeAdminCartId) clearActiveAdminCartId();
        else clearSharedCartSession();
        await CartService.getCart();
        throw new Error(
          "That cart no longer exists, so it was cleared. Please try again."
        );
      }

      await CartService.getCart();
    });
  },
  /**
   * Multi-window mat (stores/multiWindow.ts, Michael 09-24): after the mat line is added / saved, its full-bleed
   * prints get a ½" border (withMultiWindowBorder). Same cart routing as the other line-item writes.
   */
  async borderMultiWindowPrints(lineItemIds: string[]) {
    if (!lineItemIds?.length) return;
    const ids = new Set(lineItemIds);
    const activeAdminCartId = $activeAdminCartId.get();
    const { cartId: sharedCartId, secret: sharedSecret } = $sharedCartSession.get();
    const changed = ($cart.get().lineItems as any[])
      .filter(li => ids.has(li.id))
      .map(li => ({ li, itemJson: withMultiWindowBorder(li.itemJson) }))
      .filter(x => x.itemJson);
    await Promise.all(
      changed.map(({ li, itemJson }) => {
        const payload: any = { itemJson, adminCustom: !!li.adminCustom, adminEmail: li.adminEmail || "" };
        if (activeAdminCartId) return Crud.carts.updateLineItem(activeAdminCartId, li.id, payload);
        if (sharedCartId) return Crud.carts.updateLineItem(sharedCartId, li.id, { ...payload, shareSecret: sharedSecret });
        return UserApi.updateLineItem({ id: li.id, ...payload });
      })
    );
    if (changed.length) await CartService.getCart();
  },
  async updateLineItems(updates: Pick<LineItem, "id" | "quantity" | "note">[]) {
    const activeAdminCartId = $activeAdminCartId.get();
    const { cartId: sharedCartId, secret: sharedSecret } =
      $sharedCartSession.get();
    const managedCartId = activeAdminCartId || sharedCartId;
    const isSharedCart = !activeAdminCartId && Boolean(sharedCartId);
    const promises = updates.map(({ id, quantity, note }) => {
      if (managedCartId) {
        if (quantity > 0) {
          return Crud.carts.updateLineItem(managedCartId, id, {
            quantity,
            note,
            ...(isSharedCart ? { shareSecret: sharedSecret } : {}),
          } as any);
        }
        return Crud.carts.removeLineItem(
          managedCartId,
          id,
          isSharedCart ? { shareSecret: sharedSecret } : undefined
        );
      }
      if (quantity > 0) return UserApi.updateLineItem({ id, quantity, note });
      return UserApi.removeLineItem({ id });
    });
    try {
      await Promise.all(promises);
    } catch (error) {
      if (!isCartGoneError(error) || !managedCartId) throw error;
      // The managed cart is gone — clear the stale session and refresh.
      if (activeAdminCartId) clearActiveAdminCartId();
      else clearSharedCartSession();
    }
    await CartService.getCart();
  },
  async removeLineItem(id: string) {
    const activeAdminCartId = $activeAdminCartId.get();
    const { cartId: sharedCartId, secret: sharedSecret } =
      $sharedCartSession.get();
    try {
      if (activeAdminCartId) {
        await Crud.carts.removeLineItem(activeAdminCartId, id);
      } else if (sharedCartId) {
        await Crud.carts.removeLineItem(sharedCartId, id, {
          shareSecret: sharedSecret,
        });
      } else {
        await UserApi.removeLineItem({ id });
      }
    } catch (error) {
      if (!isCartGoneError(error) || (!activeAdminCartId && !sharedCartId)) {
        throw error;
      }
      // The managed cart is already gone — clearing the stale session and
      // refreshing achieves the removal from the user's point of view.
      if (activeAdminCartId) clearActiveAdminCartId();
      else clearSharedCartSession();
    }
    await CartService.getCart();
  },
  toggleCart(flag?: boolean) {
    $cart.setKey("isOpen", flag ?? !$cart.get().isOpen);
  },
};
